import { useEffect, useState } from 'react';
import type { League, SportEvent } from '../api';
import { loadLeagues } from '../sport';
import { linkProps } from '../router';
import { hasReminder, toggleReminder, useReminders } from '../reminders';

export function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

export function dayLabel(ms: number, now = Date.now()): string {
  const d = new Date(ms);
  const today = new Date(now);
  const tomorrow = new Date(now + 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Vandaag';
  if (d.toDateString() === tomorrow.toDateString()) return 'Morgen';
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

const COUNTRY_PREFIX = /^\s*[|[(]?\s*([A-Z]{2,3})\s*[|\]):\-]\s*/i;

/** "| NL | ZIGGO SPORT 2 FHD" -> { country: "NL", name: "Ziggo Sport 2 FHD" } */
export function channelDisplay(raw: string): { country?: string; name: string } {
  const m = raw.match(COUNTRY_PREFIX);
  let name = m ? raw.slice(m[0].length) : raw;
  name = name.replace(/^[|:\-\s]+|[|\s]+$/g, '').trim();
  // VOLLEDIG IN HOOFDLETTERS leest lastig: woorden netjes maken, afkortingen (ESPN, HD, F1) laten staan
  if (name.length > 4 && name === name.toUpperCase()) {
    name = name.toLowerCase().replace(/\b([a-z0-9])([a-z0-9]*)\b/g, (w, f: string, r: string) =>
      /^(hd|fhd|uhd|sd|4k|8k|f1|espn|npo|rtl|sbs|bbc|cnn|nbc|abc|tv|nl|uk|us|be|de)$/.test(w) ? w.toUpperCase() : f.toUpperCase() + r,
    );
  }
  return { country: m?.[1].toUpperCase(), name: name || raw };
}

export function ChannelName({ name, className = '' }: { name: string; className?: string }) {
  const d = channelDisplay(name);
  return (
    <span className={`ch-name ${className}`} title={name}>
      {d.country && <span className="ch-country">{d.country}</span>}
      <span className="ch-name-text">{d.name}</span>
    </span>
  );
}

export function ChannelLogo({ src, name, size = 40 }: { src: string | null; name: string; size?: number }) {
  return (
    <span className="ch-logo" style={{ width: size, height: size }} aria-hidden="true">
      {src ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
      <span className="ch-logo-fallback">{channelDisplay(name).name.slice(0, 2)}</span>
    </span>
  );
}

export function LiveBadge() {
  return <span className="live-badge"><span aria-hidden="true" className="live-dot" />LIVE</span>;
}

function sportLabel(e: SportEvent): string {
  if (e.sport === 'f1') return `Formule 1${e.session ? ` · ${e.session}` : ''}`;
  return e.competition ?? (e.sport === 'football' ? 'Voetbal' : 'Sport');
}

export function playLink(accountId: string, streamId: number, catchup?: { start: number; end: number }): string {
  const base = `/live/play/${encodeURIComponent(accountId)}/${streamId}`;
  return catchup ? `${base}?start=${catchup.start}&end=${catchup.end}` : base;
}

/** Logo van een team, of de eerste letters als er geen is. */
function Crest({ team, size }: { team: { name: string; logo: string | null }; size: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="crest" style={{ width: size, height: size }} title={team.name}>
      {team.logo && !broken ? <img src={team.logo} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} /> : <span>{team.name.slice(0, 3).toUpperCase()}</span>}
    </span>
  );
}

function statusLine(e: SportEvent): { text: string; tone: 'live' | 'soon' | 'later' } {
  const m = e.match;
  if (e.live) return { text: m?.detail || (e.sport === 'f1' && e.session ? e.session : 'Nu live'), tone: 'live' };
  const mins = Math.round((e.start - Date.now()) / 60_000);
  if (mins <= 90 && mins > 0) return { text: `Begint over ${mins} min`, tone: 'soon' };
  return { text: `${dayLabel(e.start) === 'Vandaag' ? 'Vandaag' : dayLabel(e.start)} · ${clock(e.start)}`, tone: 'later' };
}

/** Tegel voor de Home-rij "Live sport": clublogo's en stand als de wedstrijd in het schema staat. */
export function SportTile({ e }: { e: SportEvent }) {
  const ch = e.channels[0];
  const leagues = useLeagueMap();
  const lg = e.league ? leagues.get(e.league) : undefined;
  const progress = e.live ? Math.min(1, Math.max(0, (Date.now() - e.start) / (e.stop - e.start))) : undefined;
  const st = statusLine(e);
  const m = e.match;
  const showScore = m && m.state !== 'pre' && m.home?.score != null && m.away?.score != null;
  const tag = e.live ? 'LIVE' : dayLabel(e.start) === 'Vandaag' ? clock(e.start) : dayLabel(e.start).split(' ')[0].toUpperCase();
  return (
    <a className={`sport-tile ev-tile ${e.sport} ${e.live ? 'is-live' : ''}`} {...linkProps(playLink(ch.accountId, ch.streamId))} aria-label={`${e.title}, ${sportLabel(e)}, ${e.live ? 'nu live' : clock(e.start)} op ${ch.name}`}>
      <span className="ev-art">
        <span className={`ev-tag ${e.live ? 'live' : st.tone}`}>{tag}</span>
        <span className="ev-league">
          {lg?.badge && <img src={lg.badge} alt="" />}
          {sportLabel(e)}
        </span>
        {m?.home && m.away ? (
          <span className="ev-teams">
            <Crest team={m.home} size={64} />
            <span className={`ev-mid ${showScore ? 'score' : ''}`}>{showScore ? `${m.home.score} – ${m.away.score}` : 'vs'}</span>
            <Crest team={m.away} size={64} />
          </span>
        ) : (
          <span className="ev-teams">
            {lg?.badge ? <img className="ev-league-big" src={lg.badge} alt="" /> : <span className="ev-mid">{lg?.abbr ?? sportLabel(e)}</span>}
          </span>
        )}
        <span className={`ev-status ${st.tone}`}>{st.text}</span>
        {progress !== undefined && <span className="progress"><span style={{ width: `${Math.round(progress * 100)}%` }} /></span>}
      </span>
      <span className="ev-title">{e.title}</span>
      <span className="sport-tile-ch">
        <ChannelLogo src={ch.logo} name={ch.name} size={24} />
        <ChannelName name={ch.name} />
        {e.channels.length > 1 && <span>+{e.channels.length - 1}</span>}
      </span>
    </a>
  );
}

let leagueMapCache: Map<string, League> | null = null;
function useLeagueMap(): Map<string, League> {
  const [m, setM] = useState<Map<string, League>>(leagueMapCache ?? new Map());
  useEffect(() => {
    if (leagueMapCache) return;
    loadLeagues().then((ls) => {
      leagueMapCache = new Map(ls.map((l) => [l.id, l]));
      setM(leagueMapCache);
    }).catch(() => undefined);
  }, []);
  return m;
}

/** Regel in de sportlijst, met alle zenders waarop het te zien is. */
export function SportRow({ e }: { e: SportEvent }) {
  useReminders();
  const [allChannels, setAllChannels] = useState(false);
  const reminded = hasReminder(e.id);
  const catchupCh = e.live ? e.channels.find((c) => c.catchup) : undefined;
  const first = e.channels[0];
  return (
    <div className={`sport-row ${e.live ? 'is-live' : ''}`}>
      <div className="sport-row-time">
        {e.live ? <LiveBadge /> : <span>{clock(e.start)}</span>}
        <span className="subtle">tot {clock(e.stop)}</span>
      </div>
      <div className="sport-row-body">
        <span className="sport-comp">
          {sportLabel(e)}
          {e.favorite && <span className="fav-mark" aria-label="Favoriete club"> ★</span>}
        </span>
        <span className="sport-row-title">
          {e.match?.home && e.match.away ? (
            <span className="row-match">
              <Crest team={e.match.home} size={26} />
              <span>{e.match.home.name}</span>
              <strong className="row-score">{e.match.state !== 'pre' && e.match.home.score != null ? `${e.match.home.score} – ${e.match.away.score}` : 'vs'}</strong>
              <span>{e.match.away.name}</span>
              <Crest team={e.match.away} size={26} />
              {e.live && e.match.detail && <span className="row-minute">{e.match.detail}</span>}
            </span>
          ) : e.title}
        </span>
        {e.detail && <span className="sport-detail">{e.detail}</span>}
      </div>
      <div className="sport-row-channels" data-focus-group>
        {(allChannels ? e.channels : e.channels.slice(0, 4)).map((c) => (
          <a key={`${c.accountId}:${c.streamId}`} className="chip chip-channel" {...linkProps(playLink(c.accountId, c.streamId))}>
            <ChannelLogo src={c.logo} name={c.name} size={22} />
            <ChannelName name={c.name} />
          </a>
        ))}
        {e.channels.length > 4 && (
          <button className="chip" aria-expanded={allChannels} onClick={() => setAllChannels((v) => !v)} aria-label={allChannels ? 'Minder zenders tonen' : `Nog ${e.channels.length - 4} zenders tonen`}>
            {allChannels ? 'Minder' : `+${e.channels.length - 4} zenders`}
          </button>
        )}
        {catchupCh && (
          <a className="chip" {...linkProps(playLink(catchupCh.accountId, catchupCh.streamId, { start: e.start, end: e.stop }))}>⟲ Vanaf begin</a>
        )}
        {!e.live && (
          <button
            className="chip"
            aria-pressed={reminded}
            onClick={() => toggleReminder({ id: e.id, title: e.title, subtitle: sportLabel(e), start: e.start, accountId: first.accountId, streamId: first.streamId })}
            aria-label={reminded ? `Herinnering voor ${e.title} uitzetten` : `Herinner mij aan ${e.title}`}
          >
            <BellIcon /> {reminded ? 'Herinnering aan' : 'Herinner mij'}
          </button>
        )}
      </div>
    </div>
  );
}

export function BellIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ verticalAlign: '-2px' }}>
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
